import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, RefreshCw, RotateCcw, AlertTriangle, FileText, Package, Truck, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';

const OperationsDeletedList = () => {
  // Fonction utilitaire pour formater les dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState({});
  const [deleting, setDeleting] = useState({});
  const [restoreDialog, setRestoreDialog] = useState({ open: false, item: null, type: null });
  const [hardDeleteDialog, setHardDeleteDialog] = useState({ open: false, item: null, type: null });
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    loadDeletedItems();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadDeletedItems = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      const [requestsRes, offersRes, bookingsRes] = await Promise.all([
        adminAPI.getAllRequests({ deleted_only: true }),
        adminAPI.getAllOffers({ deleted_only: true }),
        adminAPI.getAllBookings({ deleted_only: true }),
      ]);

      if (!isMountedRef.current) return;

      setRequests(requestsRes.data.results || requestsRes.data || []);
      setOffers(offersRes.data.results || offersRes.data || []);
      setBookings(bookingsRes.data.results || bookingsRes.data || []);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Erreur lors du chargement des objets supprimés:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les objets supprimés',
        variant: 'destructive',
      });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const handleRestore = async () => {
    const { item, type } = restoreDialog;
    if (!item || !type) return;

    try {
      setRestoring({ ...restoring, [`${type}-${item.id}`]: true });

      let response;
      if (type === 'request') {
        response = await adminAPI.restoreRequest(item.id);
      } else if (type === 'offer') {
        response = await adminAPI.restoreOffer(item.id);
      } else if (type === 'booking') {
        response = await adminAPI.restoreBooking(item.id);
      }

      toast({
        title: 'Succès',
        description: response.data?.detail || 'Objet restauré avec succès',
      });

      setRestoreDialog({ open: false, item: null, type: null });
      await loadDeletedItems();
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de restaurer l\'objet',
        variant: 'destructive',
      });
    } finally {
      setRestoring({ ...restoring, [`${type}-${item.id}`]: false });
    }
  };

  const handleHardDelete = async () => {
    const { item, type } = hardDeleteDialog;
    if (!item || !type) return;

    try {
      setDeleting({ ...deleting, [`${type}-${item.id}`]: true });

      if (type === 'request') {
        await adminAPI.deleteRequest(item.id);
      } else if (type === 'offer') {
        await adminAPI.deleteOffer(item.id);
      } else if (type === 'booking') {
        await adminAPI.deleteBooking(item.id);
      }

      toast({
        title: 'Suppression définitive',
        description: `${getItemTypeLabel(type)} supprimée définitivement. Cette action est irréversible.`,
        variant: 'destructive',
      });

      setHardDeleteDialog({ open: false, item: null, type: null });
      await loadDeletedItems();
    } catch (error) {
      console.error('Erreur lors de la suppression définitive:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de supprimer définitivement l\'objet',
        variant: 'destructive',
      });
    } finally {
      setDeleting({ ...deleting, [`${type}-${item.id}`]: false });
    }
  };

  const getItemTypeLabel = (type) => {
    switch (type) {
      case 'request': return 'Demande';
      case 'offer': return 'Offre';
      case 'booking': return 'Mission';
      default: return 'Objet';
    }
  };

  const renderRequestCard = (request) => {
    const clientName = request.client_detail?.company_name
      || (request.client_detail?.user ? `${request.client_detail.user.first_name || ''} ${request.client_detail.user.last_name || ''}`.trim() : '')
      || request.client_detail?.user?.email?.split('@')[0]
      || 'N/A';

    return (
      <Card key={request.id} className="opacity-75 hover:opacity-100 transition-opacity">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-lg">Demande #{request.id?.slice(-8)}</CardTitle>
            </div>
            <Badge variant="destructive">Supprimée</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-muted-foreground">Client:</span>
              <span className="ml-2 font-medium">{clientName}</span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Statut:</span>
              <Badge variant="outline" className="ml-2">{request.status}</Badge>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Supprimée le:</span>
              <span className="ml-2">{formatDate(request.deleted_at)}</span>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/admin/clients/${request.client}/requests/${request.id}`)}
                  className="flex-1"
                >
                  Voir
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setRestoreDialog({ open: true, item: request, type: 'request' })}
                  disabled={restoring[`request-${request.id}`] || deleting[`request-${request.id}`]}
                  className="flex-1"
                >
                  {restoring[`request-${request.id}`] ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Restauration...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Restaurer
                    </>
                  )}
                </Button>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setHardDeleteDialog({ open: true, item: request, type: 'request' })}
                disabled={restoring[`request-${request.id}`] || deleting[`request-${request.id}`]}
                className="w-full"
              >
                {deleting[`request-${request.id}`] ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Supprimer définitivement
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderOfferCard = (offer) => {
    const transporterName = offer.transporter_detail?.organisation_name
      || offer.transporter_detail?.company_name
      || (offer.transporter_detail?.user_detail ? `${offer.transporter_detail.user_detail.first_name || ''} ${offer.transporter_detail.user_detail.last_name || ''}`.trim() : '')
      || 'N/A';

    return (
      <Card key={offer.id} className="opacity-75 hover:opacity-100 transition-opacity">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-lg">Offre #{offer.id?.slice(-8)}</CardTitle>
            </div>
            <Badge variant="destructive">Supprimée</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-muted-foreground">Transporteur:</span>
              <span className="ml-2 font-medium">{transporterName}</span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Statut:</span>
              <Badge variant="outline" className="ml-2">{offer.status}</Badge>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Prix:</span>
              <span className="ml-2 font-medium">{offer.price ? `${offer.price} GNF` : 'N/A'}</span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Supprimée le:</span>
              <span className="ml-2">{formatDate(offer.deleted_at)}</span>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/admin/clients/${offer.request_detail?.client}/requests/${offer.request}/offers/${offer.id}`)}
                  className="flex-1"
                >
                  Voir
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setRestoreDialog({ open: true, item: offer, type: 'offer' })}
                  disabled={restoring[`offer-${offer.id}`] || deleting[`offer-${offer.id}`]}
                  className="flex-1"
                >
                  {restoring[`offer-${offer.id}`] ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Restauration...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Restaurer
                    </>
                  )}
                </Button>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setHardDeleteDialog({ open: true, item: offer, type: 'offer' })}
                disabled={restoring[`offer-${offer.id}`] || deleting[`offer-${offer.id}`]}
                className="w-full"
              >
                {deleting[`offer-${offer.id}`] ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Supprimer définitivement
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderBookingCard = (booking) => {
    const clientName = booking.client?.company_name
      || (booking.client?.user ? `${booking.client.user.first_name || ''} ${booking.client.user.last_name || ''}`.trim() : '')
      || 'N/A';
    const transporterName = booking.transporter?.organisation_name
      || (booking.transporter?.user ? `${booking.transporter.user.first_name || ''} ${booking.transporter.user.last_name || ''}`.trim() : '')
      || 'N/A';

    return (
      <Card key={booking.id} className="opacity-75 hover:opacity-100 transition-opacity">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-lg">Mission #{booking.id?.slice(-8)}</CardTitle>
            </div>
            <Badge variant="destructive">Supprimée</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-muted-foreground">Client:</span>
              <span className="ml-2 font-medium">{clientName}</span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Transporteur:</span>
              <span className="ml-2 font-medium">{transporterName}</span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Statut:</span>
              <Badge variant="outline" className="ml-2">{booking.status_code}</Badge>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Prix convenu:</span>
              <span className="ml-2 font-medium">{booking.agreed_price ? `${booking.agreed_price} GNF` : 'N/A'}</span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Supprimée le:</span>
              <span className="ml-2">{formatDate(booking.deleted_at)}</span>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/admin/bookings/${booking.id}`)}
                  className="flex-1"
                >
                  Voir
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setRestoreDialog({ open: true, item: booking, type: 'booking' })}
                  disabled={restoring[`booking-${booking.id}`] || deleting[`booking-${booking.id}`]}
                  className="flex-1"
                >
                  {restoring[`booking-${booking.id}`] ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Restauration...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Restaurer
                    </>
                  )}
                </Button>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setHardDeleteDialog({ open: true, item: booking, type: 'booking' })}
                disabled={restoring[`booking-${booking.id}`] || deleting[`booking-${booking.id}`]}
                className="w-full"
              >
                {deleting[`booking-${booking.id}`] ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Supprimer définitivement
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Objets supprimés (soft delete)</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Consultez et restaurez les objets supprimés en soft delete
          </p>
        </div>
        <Button variant="outline" onClick={loadDeletedItems} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList>
          <TabsTrigger value="requests" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Demandes ({requests.length})
          </TabsTrigger>
          <TabsTrigger value="offers" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Offres ({offers.length})
          </TabsTrigger>
          <TabsTrigger value="bookings" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            Missions ({bookings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-6">
          {requests.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trash2 className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucune demande supprimée</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {requests.map(renderRequestCard)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="offers" className="mt-6">
          {offers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trash2 className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucune offre supprimée</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {offers.map(renderOfferCard)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bookings" className="mt-6">
          {bookings.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trash2 className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucune mission supprimée</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bookings.map(renderBookingCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={restoreDialog.open} onOpenChange={(open) => !open && setRestoreDialog({ open: false, item: null, type: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurer {getItemTypeLabel(restoreDialog.type)}</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir restaurer cette {getItemTypeLabel(restoreDialog.type).toLowerCase()} ?
              Elle redeviendra visible dans le flux normal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore}>
              Restaurer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={hardDeleteDialog.open}
        onOpenChange={(open) => !open && setHardDeleteDialog({ open: false, item: null, type: null })}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-destructive" />
              </div>
              <AlertDialogTitle className="text-xl text-destructive">
                ⚠️ Suppression définitive
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3 pt-2">
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 space-y-2">
                <p className="font-semibold text-destructive">
                  ATTENTION : Cette action est IRRÉVERSIBLE !
                </p>
                <p className="text-sm">
                  Vous êtes sur le point de supprimer définitivement cette {getItemTypeLabel(hardDeleteDialog.type).toLowerCase()}.
                </p>
              </div>
              <div className="space-y-2 text-sm">
                <p className="font-medium">Conséquences :</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                  <li>L'objet sera <strong className="text-destructive">perdu définitivement</strong></li>
                  <li>Il ne pourra <strong>plus jamais être restauré</strong></li>
                  <li>Toutes les données associées seront <strong>supprimées de la base de données</strong></li>
                  <li>Cette action ne peut <strong>pas être annulée</strong></li>
                </ul>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-3">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  ⚠️ Assurez-vous vraiment de vouloir procéder à cette suppression définitive.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDelete}
              className="w-full sm:w-auto bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              disabled={deleting[`${hardDeleteDialog.type}-${hardDeleteDialog.item?.id}`]}
            >
              {deleting[`${hardDeleteDialog.type}-${hardDeleteDialog.item?.id}`] ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression en cours...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Oui, supprimer définitivement
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OperationsDeletedList;
